import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
export type AssessmentAvailabilityStatus = 'pending' | 'in progress' | 'completed' | 'cancelled';

export function getAssessmentAvailabilityStatus(
  startTime: string | Date,
  endTime: string | Date,
  isDeleted = false,
  currentTime: string | Date = new Date()
): AssessmentAvailabilityStatus {
  if (isDeleted) {
    return 'cancelled';
  }

  const now = dayjs(currentTime).valueOf();
  const start = dayjs.utc(startTime).valueOf();
  const end = dayjs.utc(endTime).valueOf();

  if (now > end) {
    return 'completed';
  }

  if (now >= start && now <= end) {
    return 'in progress';
  }

  return 'pending';
}

/* eslint-disable */
export type AssessmentStatus = 'Planned' | 'On Going' | 'Completed' | 'Incomplete';

export function determineAssessmentStatus(
  startTime: string | Date,
  endTime: string | Date,
  totalRespondents: number,
  completedResponses: number,
  currentDate: Date = new Date()
): AssessmentStatus {
  const start = new Date(startTime);
  const end = new Date(endTime);

  if (currentDate < start) {
    return 'Planned';
  }

  if (currentDate >= start && currentDate <= end) {
    return 'On Going';
  }

  if (currentDate > end) {
    if (completedResponses >= totalRespondents) {
      return 'Completed';
    } else {
      return 'Incomplete';
    }
  }

  return 'Planned';
}

export function calculateDaysRemaining(endTime: string | Date): number {
  const end = new Date(endTime);
  const now = new Date();
  const diffTime = end.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

export function calculateProgressPercentage(
  totalRespondents: number,
  completedResponses: number
): number {
  if (totalRespondents === 0) return 0;
  return Math.round((completedResponses / totalRespondents) * 100);
}

export function getStatusColor(status: AssessmentStatus): string {
  switch (status) {
    case 'Planned':
      return 'blue';
    case 'On Going':
      return 'green';
    case 'Completed':
      return 'teal';
    case 'Incomplete':
      return 'red';
    default:
      return 'gray';
  }
}

export function getStatusVariant(status: AssessmentStatus): 'filled' | 'light' | 'outline' {
  switch (status) {
    case 'On Going':
      return 'filled';
    case 'Completed':
      return 'filled';
    case 'Incomplete':
      return 'filled';
    default:
      return 'light';
  }
}

export function isAssessmentOverdue(endTime: string | Date, status: AssessmentStatus): boolean {
  const end = new Date(endTime);
  const now = new Date();
  return now > end && (status === 'On Going' || status === 'Incomplete');
}

export function getAssessmentPriority(
  status: AssessmentStatus,
  endTime: string | Date,
  daysRemaining?: number
): 'high' | 'medium' | 'low' {
  if (status === 'Incomplete') return 'high';
  if (status === 'On Going' && daysRemaining !== undefined && daysRemaining <= 3) return 'high';
  if (status === 'On Going') return 'medium';
  if (status === 'Planned' && daysRemaining !== undefined && daysRemaining <= 7) return 'medium';
  return 'low';
}

export function formatAssessmentTimeline(
  status: AssessmentStatus,
  startTime: string | Date,
  endTime: string | Date,
  daysRemaining?: number
): string {
  const start = new Date(startTime);
  const end = new Date(endTime);
  const now = new Date();

  switch (status) {
    case 'Planned':
      const daysUntilStart = Math.ceil((start.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      return `Starts in ${daysUntilStart} day${daysUntilStart !== 1 ? 's' : ''}`;

    case 'On Going':
      if (daysRemaining !== undefined) {
        if (daysRemaining > 0) {
          return `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining`;
        } else if (daysRemaining === 0) {
          return 'Ends today';
        } else {
          return `Overdue by ${Math.abs(daysRemaining)} day${Math.abs(daysRemaining) !== 1 ? 's' : ''}`;
        }
      }
      return 'In progress';

    case 'Completed':
      const daysAgo = Math.floor((now.getTime() - end.getTime()) / (1000 * 60 * 60 * 24));
      return `Completed ${daysAgo} day${daysAgo !== 1 ? 's' : ''} ago`;

    case 'Incomplete':
      const overdueBy = Math.floor((now.getTime() - end.getTime()) / (1000 * 60 * 60 * 24));
      return `Overdue by ${overdueBy} day${overdueBy !== 1 ? 's' : ''}`;

    default:
      return '';
  }
}

export function getNextAction(status: AssessmentStatus, progressPercentage: number): string {
  switch (status) {
    case 'Planned':
      return 'Prepare and notify participants';
    case 'On Going':
      if (progressPercentage < 50) {
        return 'Send reminders to participants';
      } else if (progressPercentage < 80) {
        return 'Follow up with pending respondents';
      } else {
        return 'Monitor final submissions';
      }
    case 'Completed':
      return 'Review results and generate reports';
    case 'Incomplete':
      return 'Send urgent reminders to complete';
    default:
      return '';
  }
}

export function canEditAssessment(status: AssessmentStatus): boolean {
  return status === 'Planned';
}

export function canDeleteAssessment(status: AssessmentStatus): boolean {
  return status === 'Planned';
}

export function canStartEarly(status: AssessmentStatus): boolean {
  return status === 'Planned';
}

export function canExtendAssessment(status: AssessmentStatus): boolean {
  return status === 'On Going' || status === 'Incomplete';
}
/* eslint-enable */
